import { useState } from 'react';
import {
	Dialog,
	DialogContent,
	DialogTrigger,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { CustomButton } from '@/components/ui/custom/CustomButton';
import { ChevronDown } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { channelMembershipText } from './channels';
import TeamConversationAvatar from './TeamConversationAvatar';

const GroupDetailsDialog = ({
	activeTeamConversation,
	isEditing,
	handleClick,
	newTitle,
	handleTitleChange,
	handleCancelTitleChange,
	handleNewTitle,
	handleAddUsersMenuOpen,
	handleDeleteGroup,
	isDeletingGroup,
	menuOpen,
	setMenuOpen,
}) => {
	const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
	const isRunner = activeTeamConversation?.isRunner;
	const isChannel = Boolean(activeTeamConversation?.channel);

	if (isRunner) {
		return (
			<p className="max-w-fit truncate text-[15px] font-medium leading-5">
				{activeTeamConversation?.title} notes
			</p>
		);
	}

	const memberCount =
		activeTeamConversation?._users?.length ||
		activeTeamConversation?.numberOfUsers ||
		0;
	const subtitle = `${isChannel ? 'Channel · ' : ''}${memberCount} ${
		memberCount === 1 ? 'member' : 'members'
	}`;

	return (
		<Tooltip delayDuration={0}>
			<Dialog open={menuOpen} onOpenChange={setMenuOpen}>
				<TooltipTrigger asChild>
					<DialogTrigger asChild>
						<button
							type="button"
							className="group/title flex min-w-0 items-center gap-3 rounded-lg text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
						>
							<TeamConversationAvatar
								conversation={activeTeamConversation}
								size={36}
							/>
							<span className="flex min-w-0 flex-col gap-0.5">
								<span className="flex min-w-0 items-center gap-1 text-[15px] font-medium leading-5">
									<span className="truncate">
										{activeTeamConversation?.title}
									</span>
									<ChevronDown className="size-3.5 shrink-0 text-muted-foreground transition-transform group-hover/title:translate-y-px" />
								</span>
								<span className="truncate text-xs leading-tight text-muted-foreground">
									{subtitle}
								</span>
							</span>
						</button>
					</DialogTrigger>
				</TooltipTrigger>
				<TooltipContent side="bottom">
					{isChannel ? 'Get channel details' : 'Get group chat details'}
				</TooltipContent>
				<DialogContent>
					<DialogHeader>
						<DialogTitle className="px-1 text-ellipsis text-wrap break-words max-w-fit">
							{activeTeamConversation?.title}
						</DialogTitle>
					</DialogHeader>
					<Card>
						<CardContent className="pt-4 space-y-2">
							{isChannel && (
								<div className="flex flex-col justify-center py-2">
									<p className="text-md font-bold">Channel</p>
									<span className="text-sm font-normal text-muted-foreground">
										{channelMembershipText(activeTeamConversation)} You can
										mute it from the conversation menu.
									</span>
								</div>
							)}
							{!isChannel && (<>
							<div className="flex justify-between w-full py-2">
								<div className="flex flex-col justify-center">
									<p className="text-md font-bold">Title</p>
									<span className="text-sm font-normal text-muted-foreground">
										Update the title of your group chat
									</span>
								</div>
								{!isEditing && (
									<CustomButton onClick={handleClick}>
										Edit
									</CustomButton>
								)}
							</div>
							<div>
								{isEditing && (
									<div className="space-y-2">
										<Input
											autoFocus
											value={newTitle}
											onChange={handleTitleChange}
										/>
										<div className="flex justify-end space-x-2">
											<CustomButton
												onClick={
													handleCancelTitleChange
												}
											>
												Cancel
											</CustomButton>
											<CustomButton
												variant="default"
												onClick={handleNewTitle}
												disabled={
													newTitle ===
													activeTeamConversation?.title
												}
											>
												Save
											</CustomButton>
										</div>
									</div>
								)}
							</div>
							</>)}
							<Separator />
							<div className="flex justify-between w-full py-2">
								<div className="flex flex-col justify-center">
									<p className="flex flex-col text-md font-bold">
										Members
									</p>
									<span className="text-sm font-normal text-muted-foreground">
										{isChannel
											? 'See who is in this channel'
											: 'View or update the current members of your group chat'}
									</span>
								</div>
								<CustomButton onClick={handleAddUsersMenuOpen}>
									View
								</CustomButton>
							</div>
							{!isChannel && (<>
							<Separator />
							<div className="flex justify-between w-full py-2">
								<div className="flex flex-col justify-center">
									<p className="text-md font-bold text-destructive">
										Delete group
									</p>
									<span className="text-sm font-normal text-muted-foreground">
										Permanently delete this group chat for
										everyone
									</span>
								</div>
								<AlertDialog
									open={confirmDeleteOpen}
									onOpenChange={setConfirmDeleteOpen}
								>
									<AlertDialogTrigger asChild>
										<CustomButton
											variant="outline"
											className="text-destructive hover:text-destructive"
										>
											Delete
										</CustomButton>
									</AlertDialogTrigger>
									<AlertDialogContent>
										<AlertDialogHeader>
											<AlertDialogTitle>
												Delete{' '}
												{activeTeamConversation?.title}?
											</AlertDialogTitle>
											<AlertDialogDescription>
												This permanently deletes the
												group and all of its messages
												and attachments for every
												member. This cannot be undone.
											</AlertDialogDescription>
										</AlertDialogHeader>
										<AlertDialogFooter>
											<AlertDialogCancel
												disabled={isDeletingGroup}
											>
												Cancel
											</AlertDialogCancel>
											<AlertDialogAction
												className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
												disabled={isDeletingGroup}
												onClick={async (e) => {
													e.preventDefault();
													const deleted =
														await handleDeleteGroup();

													if (!deleted) {
														setConfirmDeleteOpen(
															false
														);
													}
												}}
											>
												{isDeletingGroup
													? 'Deleting...'
													: 'Delete group'}
											</AlertDialogAction>
										</AlertDialogFooter>
									</AlertDialogContent>
								</AlertDialog>
							</div>
							</>)}
						</CardContent>
					</Card>
				</DialogContent>
			</Dialog>
		</Tooltip>
	);
};

export default GroupDetailsDialog;
